/**
 * MfaVerifyFlow — shared 6-digit verify UX (HoD §3.2).
 *
 * Two surface modes via a single recovery-toggle:
 *   - TOTP: ``OtpCodeInput``, autosubmit on 6 digits.
 *   - Recovery: regular text input ``XXXX-XXXX``-shaped, manual submit.
 *
 * Errors per HoD:
 *   - 401 invalid_mfa_code → "That code is incorrect or expired." (generic;
 *     backend doesn't expose attempts_remaining today).
 *   - 423 / 429 lockout → lockout banner copy.
 *   - 401 invalid_token (mfa_pending expired) → full-card swap to
 *     expired state.
 */
import { useState } from "react";
import type { UseMutationResult } from "@tanstack/react-query";
import { Button } from "@/components/common/Button";
import { FormField } from "@/components/common/FormField";
import { Input } from "@/components/common/Input";
import { OtpCodeInput } from "@/components/auth/OtpCodeInput";
import { ApiError } from "@/lib/apiError";
import { useAuthStore } from "@/stores/useAuthStore";
import type {
  MfaVerifyResponse,
} from "@/hooks/queries/auth/useMfaVerify";

type VerifyMutation = UseMutationResult<
  MfaVerifyResponse,
  ApiError,
  { code?: string; recovery_code?: string }
>;

interface MfaVerifyFlowProps {
  verifyMutation: VerifyMutation;
  onComplete: () => void;
  onExpired: () => void;
}

type Mode = "totp" | "recovery";
type Banner =
  | { kind: "none" }
  | { kind: "lockout"; minutes: number }
  | { kind: "error"; text: string };

export function MfaVerifyFlow({
  verifyMutation,
  onComplete,
  onExpired,
}: MfaVerifyFlowProps): JSX.Element {
  const [mode, setMode] = useState<Mode>("totp");
  const [code, setCode] = useState("");
  const [recoveryCode, setRecoveryCode] = useState("");
  const [fieldError, setFieldError] = useState<string | null>(null);
  const [banner, setBanner] = useState<Banner>({ kind: "none" });
  const [expired, setExpired] = useState(false);

  const submit = (body: { code?: string; recovery_code?: string }): void => {
    setFieldError(null);
    setBanner({ kind: "none" });
    verifyMutation.mutate(body, {
      onSuccess: (data) => {
        useAuthStore.getState().loginSuccess(data.access_token);
        onComplete();
      },
      onError: (err) => {
        if (err.status === 401 && err.error === "invalid_token") {
          setExpired(true);
        } else if (err.status === 401 && mode === "recovery") {
          setFieldError(
            "That recovery code isn't valid or has been used.",
          );
          setRecoveryCode("");
        } else if (err.status === 401) {
          setFieldError("That code is incorrect or expired.");
          setCode("");
        } else if (err.status === 429) {
          // Lockout — surface retry timer if present.
          const detail = err.details as { retry_after_seconds?: number };
          const minutes = detail.retry_after_seconds
            ? Math.ceil(detail.retry_after_seconds / 60)
            : 15;
          setBanner({ kind: "lockout", minutes });
        } else {
          setBanner({
            kind: "error",
            text: "Our auth service hiccupped. Try again in a minute.",
          });
        }
      },
    });
  };

  const handleTotpSubmit = (): void => {
    if (code.length !== 6) return;
    submit({ code });
  };

  const handleRecoverySubmit = (): void => {
    if (recoveryCode.trim().length === 0) return;
    submit({ recovery_code: recoveryCode.trim() });
  };

  if (expired) {
    return (
      <div className="flex flex-col gap-4">
        <p className="text-sm text-text-secondary dark:text-brand-100/70">
          Your sign-in attempt timed out. Sign in again.
        </p>
        <Button type="button" className="w-full" onClick={onExpired}>
          Back to sign in
        </Button>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      {banner.kind === "lockout" ? (
        <div
          role="alert"
          className="rounded-md border border-amber-500/30 bg-amber-500/10 px-3 py-2 text-xs text-amber-700 dark:text-amber-300"
        >
          Too many attempts. Try again in {banner.minutes}{" "}
          {banner.minutes === 1 ? "minute" : "minutes"}.
        </div>
      ) : null}
      {banner.kind === "error" ? (
        <div
          role="alert"
          className="rounded-md border border-red-500/30 bg-red-500/10 px-3 py-2 text-xs text-red-700 dark:text-red-400"
        >
          {banner.text}
        </div>
      ) : null}

      {mode === "totp" ? (
        <>
          <FormField
            label="Code"
            htmlFor="mfa-verify-code"
            error={fieldError ?? undefined}
          >
            <OtpCodeInput
              id="mfa-verify-code"
              value={code}
              onChange={setCode}
              onAutoSubmit={handleTotpSubmit}
              hasError={fieldError !== null}
              disabled={
                verifyMutation.isPending || banner.kind === "lockout"
              }
              autoFocus
            />
          </FormField>
          <Button
            type="button"
            className="w-full"
            loading={verifyMutation.isPending}
            disabled={code.length !== 6 || banner.kind === "lockout"}
            onClick={handleTotpSubmit}
          >
            Verify and continue
          </Button>
          <button
            type="button"
            className="text-sm text-text-secondary underline-offset-2 hover:underline dark:text-brand-100/70"
            onClick={() => {
              setMode("recovery");
              setFieldError(null);
              setCode("");
            }}
          >
            Use a recovery code instead
          </button>
        </>
      ) : (
        <>
          <FormField
            label="Recovery code"
            htmlFor="mfa-verify-recovery"
            error={fieldError ?? undefined}
          >
            <Input
              id="mfa-verify-recovery"
              type="text"
              autoFocus
              autoComplete="off"
              inputMode="text"
              placeholder="XXXX-XXXX"
              hasError={fieldError !== null}
              value={recoveryCode}
              onChange={(e) => setRecoveryCode(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") handleRecoverySubmit();
              }}
              disabled={
                verifyMutation.isPending || banner.kind === "lockout"
              }
              className="font-mono uppercase tracking-wider"
            />
          </FormField>
          <Button
            type="button"
            className="w-full"
            loading={verifyMutation.isPending}
            disabled={
              recoveryCode.trim().length === 0 || banner.kind === "lockout"
            }
            onClick={handleRecoverySubmit}
          >
            Verify and continue
          </Button>
          <button
            type="button"
            className="text-sm text-text-secondary underline-offset-2 hover:underline dark:text-brand-100/70"
            onClick={() => {
              setMode("totp");
              setFieldError(null);
              setRecoveryCode("");
            }}
          >
            Use my authenticator code instead
          </button>
        </>
      )}
    </div>
  );
}
